import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Clock, TrendingUp, Timer, Hourglass } from 'lucide-react';
import { api } from '@/lib/api';
import { Spinner } from '@/components/Spinner';
import EmptyState from '@/components/EmptyState';

type DateRange = 'week' | 'month';

interface TimeAnalyticsData {
  averages: {
    avgWaitMinutes: number;
    avgWashMinutes: number;
    avgPickupMinutes: number;
  };
  washTimeTrend: Array<{ date: string; avgMinutes: number }>;
  washTimeByType: {
    sedan: number;
    suv: number;
    large_suv: number;
  };
  busyHours: Array<{ hour: number; count: number }>;
  peakHours: Array<{ start: number; end: number; avgWait: number }>;
}

const rangeLabels: Record<DateRange, string> = {
  week: 'الأسبوع',
  month: 'الشهر',
};

function formatHour(hour: number): string {
  if (hour === 0) return '12 ص';
  if (hour < 12) return `${hour} ص`;
  if (hour === 12) return '12 م';
  return `${hour - 12} م`;
}

function getBusynessLevel(count: number, max: number): { label: string; color: string } {
  if (max === 0) return { label: 'هادئ', color: 'bg-green-200 text-green-800' };
  const ratio = count / max;
  if (ratio <= 0.25) return { label: 'هادئ', color: 'bg-green-200 text-green-800' };
  if (ratio <= 0.5) return { label: 'معتدل', color: 'bg-yellow-200 text-yellow-800' };
  if (ratio <= 0.75) return { label: 'مزدحم', color: 'bg-orange-200 text-orange-800' };
  return { label: 'مزدحم جداً', color: 'bg-red-200 text-red-800' };
}

export default function TimeAnalyticsPage() {
  const [range, setRange] = useState<DateRange>('week');

  const { data, isLoading } = useQuery<TimeAnalyticsData>({
    queryKey: ['owner', 'analytics', 'times', range],
    queryFn: () => api.get(`/owner/analytics/times?range=${range}`),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const analytics = data || {
    averages: { avgWaitMinutes: 0, avgWashMinutes: 0, avgPickupMinutes: 0 },
    washTimeTrend: [],
    washTimeByType: { sedan: 0, suv: 0, large_suv: 0 },
    busyHours: [],
    peakHours: [],
  };

  const maxBusy = Math.max(...analytics.busyHours.map(h => h.count), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">تحليل الأوقات</h1>
          <p className="text-gray-500 text-sm mt-1">أوقات الانتظار والغسيل والذروة</p>
        </div>
        <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl">
          {(Object.keys(rangeLabels) as DateRange[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                range === r ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              {rangeLabels[r]}
            </button>
          ))}
        </div>
      </div>

      {/* Average Times */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Hourglass className="w-6 h-6 text-amber-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{analytics.averages.avgWaitMinutes} د</p>
          <p className="text-xs text-gray-500">متوسط الانتظار</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Timer className="w-6 h-6 text-blue-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{analytics.averages.avgWashMinutes} د</p>
          <p className="text-xs text-gray-500">متوسط الغسيل</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Clock className="w-6 h-6 text-green-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{analytics.averages.avgPickupMinutes} د</p>
          <p className="text-xs text-gray-500">متوسط الاستلام</p>
        </div>
      </div>

      {/* Wash Time Trend */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-primary-600" />
          <h2 className="text-base font-bold text-gray-900">اتجاه وقت الغسيل</h2>
        </div>
        {analytics.washTimeTrend.length === 0 ? (
          <EmptyState icon={Clock} title="لا توجد بيانات" />
        ) : (
          <div className="h-56" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.washTimeTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => {
                    const date = new Date(d);
                    return date.toLocaleDateString('ar-IQ', { weekday: 'short' });
                  }}
                />
                <YAxis tick={{ fontSize: 11 }} unit=" د" />
                <Tooltip
                  formatter={(value: number) => [`${value} دقيقة`, 'وقت الغسيل']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('ar-IQ')}
                />
                <Line
                  type="monotone"
                  dataKey="avgMinutes"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Wash Time by Vehicle Type */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <h2 className="text-base font-bold text-gray-900 mb-4">وقت الغسيل حسب النوع</h2>
        <div className="space-y-4">
          {[
            { type: 'سيدان', minutes: analytics.washTimeByType.sedan, color: 'bg-blue-500' },
            { type: 'SUV', minutes: analytics.washTimeByType.suv, color: 'bg-amber-500' },
            { type: 'SUV كبير', minutes: analytics.washTimeByType.large_suv, color: 'bg-purple-500' },
          ].map((item) => {
            const maxMinutes = Math.max(
              analytics.washTimeByType.sedan,
              analytics.washTimeByType.suv,
              analytics.washTimeByType.large_suv,
              1
            );
            return (
              <div key={item.type}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-700">{item.type}</span>
                  <span className="font-bold text-gray-900">{item.minutes} دقيقة</span>
                </div>
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${item.color} rounded-full transition-all`}
                    style={{ width: `${(item.minutes / maxMinutes) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Busy Hours */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <h2 className="text-base font-bold text-gray-900 mb-4">أوقات الذروة</h2>
        {analytics.busyHours.length === 0 ? (
          <EmptyState icon={Clock} title="لا توجد بيانات" />
        ) : (
          <>
            {/* Bar Chart */}
            <div className="h-48 mb-4" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.busyHours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 10 }}
                    tickFormatter={formatHour}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value: number) => [`${value} غسلة`, '']}
                    labelFormatter={(h) => formatHour(Number(h))}
                  />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {analytics.busyHours.map((entry, index) => {
                      const ratio = entry.count / maxBusy;
                      const fill = ratio > 0.75 ? '#ef4444' : ratio > 0.5 ? '#f97316' : ratio > 0.25 ? '#eab308' : '#22c55e';
                      return <Cell key={index} fill={fill} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Hourly List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {analytics.busyHours
                .filter(h => h.count > 0)
                .sort((a, b) => b.count - a.count)
                .map((h) => {
                  const level = getBusynessLevel(h.count, maxBusy);
                  return (
                    <div key={h.hour} className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-gray-700">{formatHour(h.hour)}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{h.count} غسلة</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${level.color}`}>
                          {level.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Best Times */}
            {analytics.peakHours.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-green-600">افضل وقت للزيارة:</span>{' '}
                  {analytics.busyHours
                    .filter(h => h.count > 0)
                    .sort((a, b) => a.count - b.count)
                    .slice(0, 3)
                    .map(h => formatHour(h.hour))
                    .join(' , ')}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
